// Undo tree visualization panel (undotree-style)

import type { UndoTree, UndoNode } from "../undo/tree.ts";
import { PALETTE, TYPOGRAPHY } from "./theme.ts";

/**
 * Configuration for the undo tree panel.
 */
export interface UndoPanelConfig {
  width: number;
  nodeRadius: number;
  levelHeight: number;
  branchSpacing: number;
}

const DEFAULT_CONFIG: UndoPanelConfig = {
  width: 200,
  nodeRadius: 6,
  levelHeight: 28,
  branchSpacing: 24,
};

/**
 * Visual representation of a tree node for layout.
 */
interface LayoutNode {
  node: UndoNode;
  x: number;
  y: number;
  depth: number;
}

/**
 * Side panel displaying the undo tree with navigation.
 */
export class UndoTreePanel {
  private container: HTMLElement;
  private panel: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: UndoPanelConfig;
  private visible: boolean = false;

  private tree: UndoTree | null = null;
  private layoutNodes: Map<string, LayoutNode> = new Map();
  private hoveredNodeId: string | null = null;
  private scrollOffset: number = 0;
  private canvasHeight: number = 200;

  private onNavigateCallback: ((nodeId: string) => void) | null = null;
  private onHoverCallback: ((nodeId: string | null) => void) | null = null;

  constructor(container: HTMLElement, config: Partial<UndoPanelConfig> = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Create panel container
    this.panel = document.createElement("div");
    this.panel.className = "undo-tree-panel";
    this.applyPanelStyles();

    // Create header
    const header = this.createHeader();
    this.panel.appendChild(header);

    // Create scrollable content area
    const content = document.createElement("div");
    content.className = "undo-tree-content";
    Object.assign(content.style, {
      overflow: "auto",
      position: "relative",
      minHeight: `${this.canvasHeight}px`,
    });

    // Create canvas for tree visualization
    this.canvas = document.createElement("canvas");
    this.canvas.className = "undo-tree-canvas";
    this.canvas.width = this.config.width - 4; // Account for border
    this.canvas.height = this.canvasHeight;
    Object.assign(this.canvas.style, {
      display: "block",
      cursor: "pointer",
      width: `${this.config.width - 4}px`,
      height: `${this.canvasHeight}px`,
    });

    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context for undo tree panel");
    this.ctx = ctx;

    content.appendChild(this.canvas);
    this.panel.appendChild(content);

    // Setup event handlers
    this.setupEventHandlers();

    // Initially hidden
    this.panel.style.display = "none";

    this.container.appendChild(this.panel);
  }

  private applyPanelStyles(): void {
    Object.assign(this.panel.style, {
      position: "absolute",
      top: "70px",
      left: "10px",
      width: `${this.config.width}px`,
      maxHeight: "calc(100vh - 90px)",
      backgroundColor: PALETTE.parchment.light,
      border: `2px solid ${PALETTE.ink.black}`,
      borderRadius: "4px",
      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.2)",
      zIndex: "200",
      display: "flex",
      flexDirection: "column",
      fontFamily: TYPOGRAPHY.handwritten,
    });
  }

  private createHeader(): HTMLElement {
    const header = document.createElement("div");
    Object.assign(header.style, {
      padding: "6px 10px",
      backgroundColor: PALETTE.parchment.dark,
      borderBottom: `2px solid ${PALETTE.ink.black}`,
      fontSize: "16px",
      fontWeight: "bold",
      color: PALETTE.ink.black,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: "0",
    });

    const title = document.createElement("span");
    title.textContent = "History";
    header.appendChild(title);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "\u00D7"; // × symbol
    Object.assign(closeBtn.style, {
      background: "none",
      border: "none",
      fontFamily: TYPOGRAPHY.ui,
      fontSize: "18px",
      cursor: "pointer",
      padding: "0 4px",
      color: PALETTE.ink.brown,
      lineHeight: "1",
    });
    closeBtn.addEventListener("click", () => this.hide());
    closeBtn.addEventListener("mouseenter", () => {
      closeBtn.style.color = PALETTE.ink.black;
    });
    closeBtn.addEventListener("mouseleave", () => {
      closeBtn.style.color = PALETTE.ink.brown;
    });
    header.appendChild(closeBtn);

    return header;
  }

  private setupEventHandlers(): void {
    this.canvas.addEventListener("mousemove", this.handleMouseMove);
    this.canvas.addEventListener("click", this.handleClick);
    this.canvas.addEventListener("mouseleave", () => {
      this.hoveredNodeId = null;
      this.render();
      if (this.onHoverCallback) {
        this.onHoverCallback(null);
      }
    });
  }

  private handleMouseMove = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const node = this.findNodeAtPosition(x, y);
    const newHoveredId = node?.node.id ?? null;

    if (newHoveredId !== this.hoveredNodeId) {
      this.hoveredNodeId = newHoveredId;
      this.render();
      // Notify listeners of hover change
      if (this.onHoverCallback) {
        this.onHoverCallback(newHoveredId);
      }
    }
  };

  private handleClick = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const layoutNode = this.findNodeAtPosition(x, y);
    if (layoutNode && this.onNavigateCallback) {
      this.onNavigateCallback(layoutNode.node.id);
    }
  };

  private findNodeAtPosition(x: number, y: number): LayoutNode | null {
    const { nodeRadius } = this.config;
    const hitRadius = nodeRadius * 2; // Larger hit area for easier clicking

    for (const layoutNode of this.layoutNodes.values()) {
      const dx = x - layoutNode.x;
      const dy = y - layoutNode.y;
      if (dx * dx + dy * dy <= hitRadius * hitRadius) {
        return layoutNode;
      }
    }

    return null;
  }

  /**
   * Update the tree and re-render.
   */
  update(tree: UndoTree): void {
    this.tree = tree;
    this.layoutTree();
    this.render();
  }

  /**
   * Set the callback for when a node is clicked.
   */
  onNavigate(callback: (nodeId: string) => void): void {
    this.onNavigateCallback = callback;
  }

  /**
   * Set the callback for when a node is hovered.
   * Called with nodeId when hovering a node, null when not hovering any node.
   */
  onHover(callback: (nodeId: string | null) => void): void {
    this.onHoverCallback = callback;
  }

  /**
   * Show the panel.
   */
  show(): void {
    this.visible = true;
    this.panel.style.display = "flex";
    if (this.tree) {
      this.layoutTree();
      this.render();
    }
  }

  /**
   * Hide the panel.
   */
  hide(): void {
    this.visible = false;
    this.panel.style.display = "none";
  }

  /**
   * Toggle visibility.
   */
  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if visible.
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Layout the tree nodes for rendering.
   * Uses depth-first traversal with horizontal positioning per-depth.
   */
  private layoutTree(): void {
    if (!this.tree) return;

    this.layoutNodes.clear();

    const { levelHeight, nodeRadius, width } = this.config;
    const centerX = (width - 4) / 2;

    // Track horizontal positions at each depth to avoid overlaps
    const depthCounters: number[] = [];
    let maxDepth = 0;

    // DFS to assign positions
    const assignPositions = (nodeId: string, depth: number, branchIndex: number): void => {
      const node = this.tree!.nodes.get(nodeId);
      if (!node) return;

      maxDepth = Math.max(maxDepth, depth);

      // Ensure we have a counter for this depth
      while (depthCounters.length <= depth) {
        depthCounters.push(0);
      }

      // Calculate x position based on branch structure
      // For simplicity, center the tree and spread branches
      const siblingCount = node.parentId
        ? (this.tree!.nodes.get(node.parentId)?.childIds.length ?? 1)
        : 1;

      let x: number;
      if (siblingCount === 1) {
        // Single child - center it
        x = centerX;
      } else {
        // Multiple siblings - spread them
        const spread = Math.min(this.config.branchSpacing * siblingCount, width - 40);
        const startX = centerX - spread / 2;
        x = startX + (branchIndex / (siblingCount - 1)) * spread;
      }

      const y = nodeRadius * 2 + depth * levelHeight;

      this.layoutNodes.set(nodeId, {
        node,
        x,
        y,
        depth,
      });

      // Layout children
      for (let i = 0; i < node.childIds.length; i++) {
        assignPositions(node.childIds[i], depth + 1, i);
      }
    };

    assignPositions(this.tree.rootId, 0, 0);

    // Resize canvas to fit content
    this.canvasHeight = Math.max(100, (maxDepth + 2) * levelHeight);
    this.canvas.height = this.canvasHeight;
    this.canvas.style.height = `${this.canvasHeight}px`;
  }

  /**
   * Render the tree visualization.
   */
  private render(): void {
    if (!this.tree || !this.visible) return;

    const ctx = this.ctx;
    const { nodeRadius, width } = this.config;

    // Clear canvas with parchment background
    ctx.fillStyle = PALETTE.parchment.light;
    ctx.fillRect(0, 0, width, this.canvasHeight);

    // Draw connections first (behind nodes)
    ctx.lineWidth = 2;

    for (const layoutNode of this.layoutNodes.values()) {
      const parentId = layoutNode.node.parentId;
      if (!parentId) continue;

      const parentLayout = this.layoutNodes.get(parentId);
      if (!parentLayout) continue;

      // Determine if this edge is on the current path
      const isOnCurrentPath = this.isNodeOnCurrentPath(layoutNode.node.id);
      const isParentOnCurrentPath = this.isNodeOnCurrentPath(parentId);

      if (isOnCurrentPath && isParentOnCurrentPath) {
        ctx.strokeStyle = PALETTE.accent.success;
        ctx.lineWidth = 2.5;
      } else {
        ctx.strokeStyle = PALETTE.ink.faded;
        ctx.lineWidth = 1.5;
      }

      ctx.beginPath();
      ctx.moveTo(parentLayout.x, parentLayout.y + nodeRadius);
      ctx.lineTo(layoutNode.x, layoutNode.y - nodeRadius);
      ctx.stroke();
    }

    // Draw nodes
    for (const layoutNode of this.layoutNodes.values()) {
      const { node, x, y } = layoutNode;
      const isSelected = node.id === this.tree!.currentId;
      const isHovered = node.id === this.hoveredNodeId;
      const isOnPath = this.isNodeOnCurrentPath(node.id);

      // Node circle
      ctx.beginPath();
      ctx.arc(x, y, nodeRadius, 0, Math.PI * 2);

      if (isSelected) {
        ctx.fillStyle = PALETTE.accent.success;
        ctx.strokeStyle = PALETTE.ink.black;
        ctx.lineWidth = 2;
      } else if (isHovered) {
        ctx.fillStyle = PALETTE.accent.amber;
        ctx.strokeStyle = PALETTE.ink.brown;
        ctx.lineWidth = 2;
      } else if (isOnPath) {
        ctx.fillStyle = PALETTE.parchment.dark;
        ctx.strokeStyle = PALETTE.ink.brown;
        ctx.lineWidth = 1.5;
      } else {
        ctx.fillStyle = PALETTE.parchment.medium;
        ctx.strokeStyle = PALETTE.ink.faded;
        ctx.lineWidth = 1;
      }

      ctx.fill();
      ctx.stroke();

      // Draw label on hover or selection
      if (isHovered || isSelected) {
        const label = node.operationLabel;
        ctx.font = `12px ${TYPOGRAPHY.ui}`;
        ctx.fillStyle = PALETTE.ink.black;
        ctx.textAlign = "center";

        // Draw label below node with background
        const labelY = y + nodeRadius + 12;
        const metrics = ctx.measureText(label);
        const labelWidth = metrics.width + 8;

        ctx.fillStyle = "rgba(255, 254, 240, 0.9)";
        ctx.fillRect(x - labelWidth / 2, labelY - 10, labelWidth, 14);

        ctx.fillStyle = PALETTE.ink.black;
        ctx.fillText(label, x, labelY);
      }
    }

    // Draw keybinding hints at bottom
    this.drawHints(ctx);
  }

  /**
   * Check if a node is on the path from root to current.
   */
  private isNodeOnCurrentPath(nodeId: string): boolean {
    if (!this.tree) return false;

    let current = this.tree.currentId;
    while (current) {
      if (current === nodeId) return true;
      const node = this.tree.nodes.get(current);
      if (!node || !node.parentId) break;
      current = node.parentId;
    }
    return false;
  }

  /**
   * Draw keyboard hints at the bottom.
   */
  private drawHints(ctx: CanvasRenderingContext2D): void {
    const { width } = this.config;
    const y = this.canvasHeight - 8;

    ctx.font = `10px ${TYPOGRAPHY.ui}`;
    ctx.fillStyle = PALETTE.ink.faded;
    ctx.textAlign = "center";
    ctx.fillText("Z: Undo  Y: Redo  K: Clear history", width / 2, y);
  }

  /**
   * Handle keyboard navigation within the panel.
   */
  handleKeyboardNavigation(key: string): boolean {
    if (!this.tree || !this.visible) return false;

    const currentNode = this.tree.nodes.get(this.tree.currentId);
    if (!currentNode) return false;

    switch (key) {
      case "ArrowUp":
        // Navigate to parent (undo)
        if (currentNode.parentId && this.onNavigateCallback) {
          this.onNavigateCallback(currentNode.parentId);
          return true;
        }
        break;
      case "ArrowDown":
        // Navigate to active child (redo)
        if (currentNode.childIds.length > 0 && this.onNavigateCallback) {
          const activeChildId = currentNode.childIds[currentNode.activeChildIndex];
          this.onNavigateCallback(activeChildId);
          return true;
        }
        break;
      case "ArrowLeft":
      case "ArrowRight":
        // Cycle through sibling branches
        if (currentNode.parentId) {
          const parent = this.tree.nodes.get(currentNode.parentId);
          if (parent && parent.childIds.length > 1 && this.onNavigateCallback) {
            const currentIndex = parent.childIds.indexOf(currentNode.id);
            const delta = key === "ArrowLeft" ? -1 : 1;
            const newIndex =
              (currentIndex + delta + parent.childIds.length) %
              parent.childIds.length;
            this.onNavigateCallback(parent.childIds[newIndex]);
            return true;
          }
        }
        break;
    }

    return false;
  }

  /**
   * Clean up.
   */
  destroy(): void {
    this.canvas.removeEventListener("mousemove", this.handleMouseMove);
    this.canvas.removeEventListener("click", this.handleClick);
    if (this.panel.parentNode) {
      this.panel.parentNode.removeChild(this.panel);
    }
  }
}
